import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  buttonClass,
  Drawer,
  ErrorState,
  Field,
  LoadingState,
  SearchSelect,
  SegmentedControl,
  useToast,
} from "@/shared/rd";
import type { CreateStoreResponse, StoreKind } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { useSellerOptionsQuery } from "../services/useSellerOptionsQuery";
import { CredentialsPanel } from "./CredentialsPanel";
import { SellerCreateForm, type CreatedSellerCredentials } from "./SellerCreateForm";
import { StoreCreateForm } from "./StoreCreateForm";

export type CreateMode = "seller" | "restaurant";
type OwnerMode = "new" | "existing";

const FORM_ID = "seller-create-form";

/**
 * Creating over the sellers list, in the design's drawer.
 *
 * - `seller` (route `sellers/new`): a new seller and its first business, with a
 *   switch between online store and restaurant.
 * - `restaurant` (route `restaurants/new`): a restaurant for a new seller or an
 *   existing one.
 *
 * A new seller's temporary login replaces the form once created and is shown
 * only here, once. The caller mounts this only while a create route is open
 * (and keys it by mode), so every open starts clean and the plans / seller
 * options are fetched only when needed.
 */
export function SellerCreateDrawer({
  mode,
  open,
  locale,
  onClose,
}: {
  mode: CreateMode;
  open: boolean;
  locale: Locale;
  onClose: () => void;
}) {
  const t = useTranslations();
  const toast = useToast();
  const [storeKind, setStoreKind] = useState<StoreKind>(
    mode === "restaurant" ? "RESTAURANT" : "DEFAULT",
  );
  const [ownerMode, setOwnerMode] = useState<OwnerMode>("new");
  const [sellerId, setSellerId] = useState("");
  const [credentials, setCredentials] = useState<CreatedSellerCredentials | null>(null);
  const [created, setCreated] = useState<CreateStoreResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Mounted already open, the drawer would appear without sliding in: open it
  // one frame after mount so the kit's transition runs.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const plansQuery = useSubscriptionsQuery();
  const loading = plansQuery.isLoading;
  const failed = Boolean(plansQuery.error);
  const plans = plansQuery.data?.items ?? [];

  const isRestaurant = storeKind === "RESTAURANT";
  const done = credentials !== null || created !== null;
  const usesStoreForm = mode === "restaurant" && ownerMode === "existing";
  const canSubmit = !done && !loading && !failed && (!usesStoreForm || sellerId !== "");

  const title = credentials
    ? t(keys.admin.temporaryCredentialsTitle)
    : created
      ? t(keys.admin.restaurantCreateSuccess)
      : t(mode === "restaurant" ? keys.admin.restaurantCreateTitle : keys.admin.sellerCreateTitle);
  const sub = done
    ? created
      ? `${created.store.storeName} · ${created.seller.name}`
      : undefined
    : t(mode === "restaurant" ? "admin.restaurantCreateDrawerSub" : "admin.sellerCreateDrawerSub");

  const submitLabel = t(
    isRestaurant ? keys.admin.restaurantCreateAction : keys.admin.onlineStoreCreateAction,
  );

  return (
    <Drawer
      open={open && entered}
      onClose={onClose}
      // The temporary password is shown once: only the close button may dismiss it.
      dismissible={credentials === null}
      closeLabel={t(keys.common.close)}
      width={560}
      crumb={`${t(keys.admin.sellerAccountsTitle)} / ${t(
        mode === "restaurant" ? keys.admin.restaurantCreateTitle : keys.admin.sellerCreateAction,
      )}`}
      badge={mode === "restaurant" ? "servicebell" : "contacts"}
      title={title}
      sub={sub}
      actions={
        canSubmit ? (
          <Button
            variant="primary"
            size="sm"
            type="submit"
            form={FORM_ID}
            loading={submitting}
            loadingLabel={t("admin.businessCreating")}
          >
            {submitLabel}
          </Button>
        ) : null
      }
    >
      {loading ? (
        <LoadingState
          rows={0}
          title={t(keys.shell.loadingTitle, { page: t(keys.admin.subscriptionsTitle).toLocaleLowerCase(locale) })}
          sub={t(keys.shell.loadingSub)}
        />
      ) : failed ? (
        <ErrorState
          title={t(keys.shell.errorTitle, { page: t(keys.admin.subscriptionsTitle).toLocaleLowerCase(locale) })}
          sub={t(keys.shell.errorSub)}
          onRetry={() => void plansQuery.refetch()}
          retryLabel={t(keys.shell.retry)}
        />
      ) : credentials ? (
        <CredentialsPanel credentials={credentials} locale={locale} />
      ) : created ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link
            to={`/${locale}/admin/stores/${created.store.id}`}
            className={buttonClass("primary", "sm")}
            style={{ textDecoration: "none", color: "var(--rd-on-primary)" }}
          >
            {t(keys.admin.viewCreatedBusiness)}
          </Link>
          <Link
            to={`/${locale}/admin/sellers/${created.seller.id}`}
            className={buttonClass("ghost", "sm")}
            style={{ textDecoration: "none" }}
          >
            {t(keys.admin.viewSeller)}
          </Link>
        </div>
      ) : (
        <>
          {mode === "seller" ? (
            <Field label={t(keys.admin.businessTypeLabel)}>
              <SegmentedControl<StoreKind>
                label={t(keys.admin.businessTypeLabel)}
                value={storeKind}
                onChange={setStoreKind}
                options={[
                  { value: "DEFAULT", label: t(keys.admin.onlineStoreKindLabel) },
                  { value: "RESTAURANT", label: t(keys.admin.restaurantKindLabel) },
                ]}
              />
            </Field>
          ) : (
            <Field label={t(keys.admin.restaurantOwnerModeLabel)}>
              <SegmentedControl<OwnerMode>
                label={t(keys.admin.restaurantOwnerModeLabel)}
                value={ownerMode}
                onChange={(value) => {
                  setOwnerMode(value);
                  setSellerId("");
                }}
                options={[
                  { value: "new", label: t(keys.admin.restaurantOwnerNew) },
                  { value: "existing", label: t(keys.admin.restaurantOwnerExisting) },
                ]}
              />
            </Field>
          )}

          {usesStoreForm ? (
            <>
              <ExistingSellerPicker value={sellerId} onChange={setSellerId} locale={locale} />
              {sellerId ? (
                <StoreCreateForm
                  key={sellerId}
                  formId={FORM_ID}
                  sellerId={sellerId}
                  locale={locale}
                  plans={plans}
                  storeKind="RESTAURANT"
                  onSuccess={(result) => {
                    toast.show({ message: t("admin.businessCreatedToast", { name: result.store.storeName }) });
                    setCreated(result);
                  }}
                  onSubmittingChange={setSubmitting}
                />
              ) : (
                <Alert tone="info">{t(keys.admin.restaurantOwnerSelectHint)}</Alert>
              )}
            </>
          ) : (
            <SellerCreateForm
              formId={FORM_ID}
              locale={locale}
              plans={plans}
              storeKind={storeKind}
              onCreated={setCredentials}
              onSubmittingChange={setSubmitting}
            />
          )}
        </>
      )}
    </Drawer>
  );
}

/** The owner picker for a restaurant of an existing seller. Fetches the
 *  seller options only when this mode is chosen. */
function ExistingSellerPicker({
  value,
  onChange,
  locale,
}: {
  value: string;
  onChange: (sellerId: string) => void;
  locale: Locale;
}) {
  const t = useTranslations();
  const { data, isLoading, error, refetch } = useSellerOptionsQuery();
  const page = t(keys.admin.sellersTitle).toLocaleLowerCase(locale);
  if (isLoading) {
    return <LoadingState rows={0} title={t(keys.shell.loadingTitle, { page })} sub={t(keys.shell.loadingSub)} />;
  }
  if (error) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={t(keys.shell.retry)}
      />
    );
  }
  return (
    <Field label={t(keys.admin.restaurantOwnerExisting)} htmlFor="restaurant-owner">
      <SearchSelect
        id="restaurant-owner"
        label={t(keys.admin.restaurantOwnerExisting)}
        value={value}
        onChange={onChange}
        placeholder={t(keys.admin.restaurantOwnerSelectHint)}
        emptyLabel={t(keys.common.noResults)}
        options={(data?.items ?? []).map((seller) => ({ value: seller.id, label: seller.name }))}
      />
    </Field>
  );
}
